import { Container, Graphics } from 'pixi.js';

/**
 * Hoof dust. Pure render theatre — it reads sim state and never touches
 * it, and its randomness is local so it can't perturb determinism.
 */

interface Mote {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
}

const DUST_COLORS = [0xc8ab7f, 0xb0906a, 0xd8c49c];

/**
 * Hoof dust. Scenes call `emit` when a stride lands; `update` ages the
 * motes. One Graphics redrawn per frame — cheap at this particle count.
 */
export class DustField {
  private readonly gfx = new Graphics();
  private readonly motes: Mote[] = [];
  private readonly capacity: number;
  /** Local LCG: render-only randomness, never the sim's forks. */
  private seed = 0x9e3779b9;

  constructor(parent: Container, capacity = 40) {
    this.capacity = capacity;
    parent.addChild(this.gfx);
  }

  private random(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4_294_967_296;
  }

  /** A puff at the hooves. `strength` scales count and spread with speed. */
  emit(x: number, y: number, strength = 1): void {
    const count = 1 + Math.round(this.random() * 2 * Math.max(0, Math.min(1.5, strength)));
    for (let index = 0; index < count; index += 1) {
      if (this.motes.length >= this.capacity) this.motes.shift();
      const maxLife = 0.35 + this.random() * 0.35;
      this.motes.push({
        x: x + (this.random() - 0.5) * 6,
        y: y - this.random() * 3,
        // Dust is thrown backwards off the hoof and drifts up.
        vx: -18 - this.random() * 26 * strength,
        vy: -6 - this.random() * 10,
        life: maxLife,
        maxLife,
        size: this.random() < 0.4 ? 2 : 1,
      });
    }
  }

  update(dtS: number): void {
    this.gfx.clear();
    for (let index = this.motes.length - 1; index >= 0; index -= 1) {
      const mote = this.motes[index]!;
      mote.life -= dtS;
      if (mote.life <= 0) {
        this.motes.splice(index, 1);
        continue;
      }
      mote.x += mote.vx * dtS;
      mote.y += mote.vy * dtS;
      mote.vy += 14 * dtS; // settles back down
      mote.vx *= 1 - 1.6 * dtS;
      const t = mote.life / mote.maxLife;
      const color = DUST_COLORS[index % DUST_COLORS.length]!;
      this.gfx
        .rect(Math.round(mote.x), Math.round(mote.y), mote.size, mote.size)
        .fill({ color, alpha: 0.55 * t });
    }
  }

  clear(): void {
    this.motes.length = 0;
    this.gfx.clear();
  }

  destroy(): void {
    this.gfx.destroy();
  }
}

/** Emit one puff per stride: tracks distance and fires on each footfall. */
export class StrideEmitter {
  private lastEmitM = 0;

  /** Call every frame with the horse's distance; returns true on a footfall. */
  step(distanceM: number, strideM = 2.2): boolean {
    if (distanceM < this.lastEmitM) this.lastEmitM = distanceM; // replay/reset
    if (distanceM - this.lastEmitM < strideM) return false;
    this.lastEmitM = distanceM;
    return true;
  }

  reset(): void {
    this.lastEmitM = 0;
  }
}
